import React, { useState, useEffect, useCallback } from 'react';
import { dynamicApi } from '../../services/api';
import { formatDateOnly } from '../../utils/dateTime';

/**
 * Transmittal Log Entry section of a Submittal Package.
 *
 * READ ONLY, deliberately. This shows which transmittals a package appears on and nothing
 * more - transmittals are raised and edited on the Transmittals page, not from inside a
 * package. A transmittal can carry several packages, so it is not owned by any one of them
 * and should not be created from one.
 *
 * The link lives in the Transmittal_Package join table, which is also where an index sheet
 * package will join once it is a stored thing rather than a set of report parameters.
 *
 * Served by the generic dynamic API: these are plain project tables registered in
 * AppCfg_FieldMetadata, so there is no bespoke controller. FK columns arrive with a
 * companion "<field>_Display" holding the label, which is what the table renders.
 */
const TransmittalLogSection = ({ packageId, notify, hidden = false }) => {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!packageId) return;
    setLoading(true);
    try {
      const linkRes  = await dynamicApi.getFiltered('Transmittal_Package', 'DSPackage', packageId);
      const ids = (linkRes.data ?? []).map(l => l.transmittal_Log).filter(Boolean);
      if (ids.length === 0) { setRows([]); return; }
      const fetched = await Promise.all(ids.map(id => dynamicApi.getById('Transmittal_Log', id)));
      setRows(fetched.map(r => r.data).filter(Boolean));
    } catch (err) {
      notify?.error(`Failed to load transmittals: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [packageId, notify]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="stui-col-24"><div className="stui-detail-empty">Loading transmittals…</div></div>;
  }

  if (rows.length === 0) {
    return (
      <div className="stui-col-24">
        <div className="stui-detail-empty">This package is not on any transmittal.</div>
      </div>
    );
  }

  return (
    <div className="stui-col-24" hidden={hidden}>
      <div className="stui-rpt-preview-table-wrap">
        <table className="stui-rpt-preview-table">
          <thead className="stui-rpt-preview-thead">
            <tr>
              <th className="stui-rpt-preview-th">Transmittal No.</th>
              <th className="stui-rpt-preview-th">Title</th>
              <th className="stui-rpt-preview-th">Transfer Company</th>
              <th className="stui-rpt-preview-th">Status</th>
              <th className="stui-rpt-preview-th">Transferred</th>
              <th className="stui-rpt-preview-th">Response</th>
              <th className="stui-rpt-preview-th">Aging</th>
              <th className="stui-rpt-preview-th">Attachment</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id ?? r.ID}>
                <td className="stui-rpt-preview-td">{r.transmittal_Number}</td>
                <td className="stui-rpt-preview-td">{r.title || ''}</td>
                <td className="stui-rpt-preview-td">{r.transfer_Party_Display || ''}</td>
                <td className="stui-rpt-preview-td">{r.transmittalStatus_Display || ''}</td>
                <td className="stui-rpt-preview-td">{formatDateOnly(r.transfer_Date)}</td>
                <td className="stui-rpt-preview-td">{formatDateOnly(r.response_Date)}</td>
                <td className="stui-rpt-preview-td">{r.aging ?? ''}</td>
                <td className="stui-rpt-preview-td">
                  {r.attachment_Url
                    ? <a href={r.attachment_Url} target="_blank" rel="noopener noreferrer">Open</a>
                    : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TransmittalLogSection;
